import React, { useEffect } from "react";
import {
  AUTH_LOCAL_STORAGE_KEY,
  parseJwtEncodedTokenAndGetReduxState,
} from "../../services/types/auth-types";
import { logoutUser, setAuthState } from "../../services/actions/auth-actions";
import { useDispatch, useSelector } from "react-redux";
import { removeUserInfo } from "../../services/actions/user-actions";
import { clearWebs, setActiveWeb } from "../../services/actions/web-actions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes } from "../../navigation/navigation-types";
import WelcomePng from "../../assets/images/welcome.png";
import DeletePng from "../../assets/images/delete.png";
import { Stack, Typography } from "@mui/material";
import CustomButton from "../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { getAuthenticationStatus } from "../../services/selectors/user-selectors";
import CircularProgressBox from "../../components/CircularProgressBox";
import { GlobalVars } from "../../global";

const EshopRychleLogin: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(getAuthenticationStatus);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [screenText, setScreenText] = React.useState<string>("");
  const [isError, setIsError] = React.useState<boolean>(false);

  useEffect(() => {
    const removeOldToken = async () => {
      dispatch(removeUserInfo());
      dispatch(clearWebs());
      dispatch(logoutUser());
      localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
    };

    const login = async () => {
      setScreenText("");
      setIsError(false);
      try {
        await removeOldToken();

        // Get magic link token from URL parameter
        const magicToken = searchParams.get("token");

        if (!magicToken) {
          throw new Error("Missing token in URL");
        }

        // Validate magic link token with backend and get JWT
        const response = await fetch(
          `${GlobalVars.SERVER_ENDPOINT}/eshop-rychle/validate-magic-link`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token: magicToken }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message || "Token validation failed"
          );
        }

        const data = await response.json();
        const jwtToken = data.token;
        const websiteId = data.websiteId;

        if (!jwtToken) {
          throw new Error("No JWT token received from server");
        }

        // Store JWT token in localStorage
        localStorage.setItem(AUTH_LOCAL_STORAGE_KEY, jwtToken);

        // Parse JWT token and set Redux state
        const state = parseJwtEncodedTokenAndGetReduxState(jwtToken);
        dispatch(setAuthState(state));

        // Set active website if websiteId is provided
        if (websiteId) {
          dispatch(setActiveWeb("", websiteId));
        }

        // Set welcome message
        setScreenText(
          t("eshopRychleFirstTouchScreen.title") ??
          "Vítejte v Ověřený web!"
        );

        // If user is confirmed and from eshop-rychle, navigate to profile to set password
        if (
          state.parsedToken.isActivationConfirmed &&
          state.parsedToken.registeredFrom === "eshop-rychle"
        ) {
          // Navigate to profile with password change tab selected
          navigate(Routes.profile, {
            state: { preselectedTab: "connection" },
          });
        }
      } catch (error) {
        await removeOldToken();
        console.error("Eshop-rychle login error:", error);
        setIsError(true);
        setScreenText(
          t("eshopRychleFirstTouchScreen.error") ??
          "Přihlášení se nezdařilo. Zkuste to prosím znovu."
        );
      }
    };

    login();
  }, [dispatch, searchParams, navigate, t]);

  const handleButtonClick = async () => {
    navigate(Routes.profile, {
      state: { preselectedTab: "connection" },
    });
  };

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Stack px={20} justifyContent='center' alignItems='center'>
        <img src={isError ? DeletePng : WelcomePng} alt='welcome' />
        <Stack
          maxWidth={800}
          spacing={1}
          justifyContent='center'
          alignItems='center'
          textAlign='center'
          my={1}
        >
          <Typography fontSize={32} fontWeight={500}>
            {screenText}
          </Typography>
          {!isError && (
            <>
              <Typography>
                {t("eshopRychleFirstTouchScreen.description1") ??
                  "Váš účet byl úspěšně vytvořen."}
              </Typography>
              <Typography>
                {t("eshopRychleFirstTouchScreen.description2") ??
                  "Prosím nastavte si heslo pro přihlášení do administrace."}
              </Typography>
            </>
          )}
        </Stack>
        {!isAuthenticated && <CircularProgressBox />}
        {!isError && isAuthenticated && (
          <CustomButton
            title={
              t("eshopRychleFirstTouchScreen.button") ??
              "Nastavit heslo"
            }
            onClick={handleButtonClick}
            size='medium'
            style={{
              paddingLeft: 20,
              paddingRight: 20,
            }}
          />
        )}
      </Stack>
    </div>
  );
};

export default EshopRychleLogin;
